import { api, ApiError } from '../../lib/api';
import type { Account } from '../../types/account';
import type { CurrentUser, GridPreferences, UserRole } from '../../types/user';
import type { Rollout, RolloutStatus } from '../../types/rollout';
import type { FranchiseLocationProfile } from '../../types/franchiseLocationProfile';
import type { FranchiseProfile } from '../../types/franchiseProfile';
import type {
  AccountCampaign,
  AccountCampaignBulkAction,
  AccountCampaignBulkResult,
  AccountCampaignInlinePatch,
  LocationSyncPreview,
} from '../../types/accountCampaign';
import type { AuditTimelineEntry } from '../../types/auditTimeline';
import type { AssetLabel, Campaign } from '../../types/campaign';
import type { PromotionPlan } from '../../types/promotionPlan';

// Rollout payloads -----------------------------------------------------------

export type RolloutCreatePayload = {
  brandAccountId: string;
  name: string;
  slug?: string;
  defaultStartDate: string | null;
  defaultEndDate: string | null;
  defaultCampaignId: string | null;
  notes: string | null;
  // Pre-P2b-3 polish round 5 §2.3: rollout-level "Mmm yy" label.
  monthYearLabel: string;
};

export type RolloutUpdatePayload = Partial<{
  name: string;
  slug: string;
  defaultStartDate: string | null;
  defaultEndDate: string | null;
  defaultCampaignId: string | null;
  notes: string | null;
  monthYearLabel: string;
}>;

async function maybe<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export async function listRollouts(options: { brandAccountId?: string } = {}): Promise<Rollout[]> {
  const qs = options.brandAccountId
    ? `?brandAccountId=${encodeURIComponent(options.brandAccountId)}`
    : '';
  const res = await api<{ rollouts: Rollout[] }>(`/api/rollouts${qs}`);
  return res.rollouts;
}

export async function getRollout(id: string): Promise<Rollout | null> {
  return maybe(async () => {
    const res = await api<{ rollout: Rollout }>(`/api/rollouts/${id}`);
    return res.rollout;
  });
}

export async function createRollout(payload: RolloutCreatePayload): Promise<Rollout> {
  const res = await api<{ rollout: Rollout }>('/api/rollouts', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return res.rollout;
}

export async function updateRollout(id: string, patch: RolloutUpdatePayload): Promise<Rollout> {
  const res = await api<{ rollout: Rollout }>(`/api/rollouts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return res.rollout;
}

export async function changeRolloutStatus(id: string, status: RolloutStatus): Promise<Rollout> {
  const res = await api<{ rollout: Rollout }>(`/api/rollouts/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status }),
  });
  return res.rollout;
}

// HARD delete (Deletion spec) — permanent + irreversible. Removes all
// AccountCampaign rows + the Rollout, and unlocks the parent Plan when the
// Rollout was spawned from one. Soft-archive is changeRolloutStatus, NOT
// this. Returns the impact summary for post-delete navigation/UX.
export type HardDeleteRolloutResult = {
  deletedRolloutId: string;
  accountCampaignCount: number;
  promotionPlanId: string | null;
  planUnlocked: boolean;
};

export async function hardDeleteRollout(
  id: string,
): Promise<HardDeleteRolloutResult> {
  return api<HardDeleteRolloutResult>(`/api/rollouts/${id}`, {
    method: 'DELETE',
  });
}

// Rows -----------------------------------------------------------------------

export type RolloutRowsResponse = {
  rollout: Rollout;
  rows: AccountCampaign[];
  accounts: Account[];
  // Keyed by accountId. Missing entries mean no FranchiseLocationProfile exists
  // for that location -- master grid silently falls back to Account.name in
  // the Location column.
  locationProfiles: FranchiseLocationProfile[];
  // P2b-3 §3 / §4: the brand's FranchiseProfile, included so the role grids
  // can render Meta names + Google ad labels via the client-side template
  // resolver without a second fetch. Null when the brand has no profile
  // attached (legacy / mid-setup state).
  brandFranchiseProfile: FranchiseProfile | null;
};

// P2b-3 §2.3: optional gridFilter pre-filters rows server-side to those
// that pass the Meta or Google grid gate. Omitted → master-grid behaviour.
export type GetRolloutRowsOptions = {
  gridFilter?: 'meta' | 'google';
};

export async function getRolloutRows(
  id: string,
  options: GetRolloutRowsOptions = {},
): Promise<RolloutRowsResponse> {
  const qs = options.gridFilter
    ? `?gridFilter=${encodeURIComponent(options.gridFilter)}`
    : '';
  return api<RolloutRowsResponse>(`/api/rollouts/${id}/rows${qs}`);
}

export async function patchRow(
  rolloutId: string,
  rowId: string,
  patch: AccountCampaignInlinePatch,
): Promise<AccountCampaign> {
  const res = await api<{ row: AccountCampaign }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  return res.row;
}

export async function patchRowBudget(
  rolloutId: string,
  rowId: string,
  args: { platform: 'meta' | 'google'; monthlyCents: number | null; patchAccount: boolean },
): Promise<AccountCampaign> {
  const res = await api<{ row: AccountCampaign }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/budget`,
    { method: 'POST', body: JSON.stringify(args) },
  );
  return res.row;
}

// Stage 1 §13.15. Fire-and-forget stub-write for the Row Editor's
// Local Assets browser. Same idempotency contract as the campaigns-
// side variant — second call with the same stubs is a no-op.
export async function addRowLocalAssetLabelStubs(
  rolloutId: string,
  rowId: string,
  stubs: AssetLabel[],
): Promise<AccountCampaign> {
  const res = await api<{ row: AccountCampaign }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/local-asset-label-stubs`,
    { method: 'POST', body: JSON.stringify({ stubs }) },
  );
  return res.row;
}

// Stage 1 §13.17. Row-scoped Local Assets orphan prune. No cascade —
// localAssetLabels are row-scoped by construction. Returns the
// updated row so the caller can refresh its local state.
export async function pruneRowLocalAssetLabels(
  rolloutId: string,
  rowId: string,
): Promise<AccountCampaign> {
  const res = await api<{ row: AccountCampaign }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/prune-orphan-local-labels`,
    { method: 'POST', body: '{}' },
  );
  return res.row;
}

// OrganicAssets §8 / §9.3. Organic mirrors of the Local stub-write + prune.
export async function addRowOrganicAssetLabelStubs(
  rolloutId: string,
  rowId: string,
  stubs: AssetLabel[],
): Promise<AccountCampaign> {
  const res = await api<{ row: AccountCampaign }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/organic-asset-label-stubs`,
    { method: 'POST', body: JSON.stringify({ stubs }) },
  );
  return res.row;
}

export async function pruneRowOrganicAssetLabels(
  rolloutId: string,
  rowId: string,
): Promise<AccountCampaign> {
  const res = await api<{ row: AccountCampaign }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/prune-orphan-organic-labels`,
    { method: 'POST', body: '{}' },
  );
  return res.row;
}

export async function bulkAction(
  rolloutId: string,
  action: AccountCampaignBulkAction,
): Promise<AccountCampaignBulkResult> {
  return api<AccountCampaignBulkResult>(`/api/rollouts/${rolloutId}/rows/bulk`, {
    method: 'POST',
    body: JSON.stringify(action),
  });
}

export async function deleteSecondaryRow(rolloutId: string, rowId: string): Promise<void> {
  await api<{ ok: true }>(`/api/rollouts/${rolloutId}/rows/${rowId}`, { method: 'DELETE' });
}

// Row content editing (P2b-2 §7.4). Sparse PATCH of the snapshotted content
// fields -- only keys present are written. Server responds with the full
// updated row.
export type RowContentPatch = Partial<{
  // Consolidation §4: offer URLs moved to the Plan — no longer per-row.
  landingPageType: 'offer' | 'sale' | 'location' | 'custom';
  customLandingPageUrl: string | null;
  metaLandingPageOverride: AccountCampaign['metaLandingPageOverride'];
  googleLandingPageOverride: AccountCampaign['googleLandingPageOverride'];
  metaAdCopy: AccountCampaign['metaAdCopy'];
  googleAssets: AccountCampaign['googleAssets'];
  utmConfig: AccountCampaign['utmConfig'];
  metaAssetsFolderUrl: string | null;
  googleAssetsFolderUrl: string | null;
  otherAssetsFolderUrl: string | null;
  metaAssetLabels: AccountCampaign['metaAssetLabels'];
}>;

export async function patchRowContent(
  rolloutId: string,
  rowId: string,
  patch: RowContentPatch,
): Promise<AccountCampaign> {
  const res = await api<{ row: AccountCampaign }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/content`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  return res.row;
}

export async function refreshRowFromCampaign(
  rolloutId: string,
  rowId: string,
): Promise<AccountCampaign> {
  const res = await api<{ row: AccountCampaign }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/refresh-from-campaign`,
    { method: 'POST', body: '{}' },
  );
  return res.row;
}

// Granular Refresh spec §4. Bulk partial re-snapshot. Atomic server-side
// (all-or-nothing): resolves with the count + echoed fieldKeys on success,
// throws ApiError on any validation/atomic failure (caller surfaces it +
// offers retry — the modal stays open).
export async function refreshFields(
  rolloutId: string,
  rowIds: string[],
  fieldKeys: string[],
): Promise<{ refreshed: number; fieldKeys: string[] }> {
  return api<{ refreshed: number; fieldKeys: string[] }>(
    `/api/rollouts/${rolloutId}/refresh-fields`,
    { method: 'POST', body: JSON.stringify({ rowIds, fieldKeys }) },
  );
}

// Single-row fetch (P2b-2 §7.4). Row editor loads the row fresh rather than
// peeling it off the master grid payload so deep-linking to a row works.
export async function getRow(rolloutId: string, rowId: string): Promise<AccountCampaign> {
  const res = await api<{ row: AccountCampaign }>(`/api/rollouts/${rolloutId}/rows/${rowId}`);
  return res.row;
}

// Row lifecycle transitions (P2b-2 §6.7). Both POST endpoints take no body --
// the rollout + row ids plus the verb fully specify the action. Server writes
// a single audit entry per call with context='mark_row_as_opt_out' /
// 'reverse_row_opt_out' so history pages can special-case the rendering.
export async function markRowAsOptOut(
  rolloutId: string,
  rowId: string,
): Promise<AccountCampaign> {
  const res = await api<{ row: AccountCampaign }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/opt-out`,
    { method: 'POST', body: '{}' },
  );
  return res.row;
}

export async function reverseRowOptOut(
  rolloutId: string,
  rowId: string,
): Promise<AccountCampaign> {
  const res = await api<{ row: AccountCampaign }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/reverse-opt-out`,
    { method: 'POST', body: '{}' },
  );
  return res.row;
}

// Save Alternate as Campaign (Pre-P2b-3 §5.4). Server creates a new
// Campaign from the row's snapshotted content + AM-chosen name /
// description / shareTargets, then repoints the row at the new
// Campaign and clears campaignNameOverride.
export type SaveAsCampaignArgs = {
  name: string;
  description?: string | null;
  shareTargets: Array<{ accountId: string }>;
};

// Server tries to flip the new Campaign to 'active' (Pre-P2b-3 polish
// Fix 1). When the snapshotted content fails active validation the
// Campaign stays as 'draft' and validationWarnings carries the missing
// fields. Empty array = activated cleanly.
export type SaveAsCampaignValidationWarning = {
  path: string;
  message: string;
};

export type SaveAsCampaignResult = {
  campaign: Campaign;
  row: AccountCampaign;
  validationWarnings: SaveAsCampaignValidationWarning[];
};

export async function saveAsCampaign(
  rolloutId: string,
  rowId: string,
  args: SaveAsCampaignArgs,
): Promise<SaveAsCampaignResult> {
  return api<SaveAsCampaignResult>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/save-as-campaign`,
    { method: 'POST', body: JSON.stringify(args) },
  );
}

// Duplicate a row (P2b-2 §6.8, Chunk C). Server returns the newly-created
// secondary row shape; caller refetches the grid to pick it up alongside
// the source row.
export async function duplicateRow(
  rolloutId: string,
  rowId: string,
): Promise<AccountCampaign> {
  const res = await api<{ row: AccountCampaign }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/duplicate`,
    { method: 'POST', body: '{}' },
  );
  return res.row;
}

// Row audit timeline (P2b-2 §7.5). Returns entries newest-first in the
// wire shape { field, before, after } declared in src/types/auditTimeline.
export async function getRowAuditLog(
  rolloutId: string,
  rowId: string,
): Promise<AuditTimelineEntry[]> {
  const res = await api<{ entries: AuditTimelineEntry[] }>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/audit`,
  );
  return res.entries;
}

// Organic Assets + Auto Folder Creation -------------------------------------
// OrganicAssets_AutoFolderCreation_Spec §3 (creation) + §4 (presence refresh).
// All three return the service result object directly (sendJson, unwrapped).

export type CreateRolloutAssetFoldersResult = {
  rowsWithCampaign: number;
  rowsSkippedNoCampaign: number;
  foldersCreated: number;
  rowsUpdated: number;
  failures: Array<{ accountCampaignId: string; locationName: string; reason: string }>;
};

export async function createRolloutAssetFolders(
  rolloutId: string,
): Promise<CreateRolloutAssetFoldersResult> {
  return api<CreateRolloutAssetFoldersResult>(
    `/api/rollouts/${rolloutId}/create-asset-folders`,
    { method: 'POST', body: '{}' },
  );
}

export type CreateRowAssetFoldersResult = {
  foldersCreated: number;
  localAssetsUrl: string | null;
  organicAssetsUrl: string | null;
};

export async function createRowAssetFolders(
  rolloutId: string,
  rowId: string,
): Promise<CreateRowAssetFoldersResult> {
  return api<CreateRowAssetFoldersResult>(
    `/api/rollouts/${rolloutId}/rows/${rowId}/create-asset-folders`,
    { method: 'POST', body: '{}' },
  );
}

export type RefreshFoldersResult = {
  rowsChecked: number;
  rowsUpdated: number;
  skipped: number;
};

// mode 'auto' honours the 5-min per-row freshness window + writes no audit;
// 'manual' force-checks every row + writes one summary audit entry.
export async function refreshFolders(
  rolloutId: string,
  mode: 'auto' | 'manual',
): Promise<RefreshFoldersResult> {
  return api<RefreshFoldersResult>(`/api/rollouts/${rolloutId}/refresh-folders`, {
    method: 'POST',
    body: JSON.stringify({ mode }),
  });
}

// Location sync --------------------------------------------------------------

export async function getLocationSyncPreview(rolloutId: string): Promise<LocationSyncPreview> {
  const res = await api<{ preview: LocationSyncPreview }>(
    `/api/rollouts/${rolloutId}/location-sync-preview`,
  );
  return res.preview;
}

export async function commitLocationSync(
  rolloutId: string,
): Promise<{ added: number; softDeleted: number }> {
  return api<{ added: number; softDeleted: number }>(
    `/api/rollouts/${rolloutId}/location-sync-commit`,
    { method: 'POST', body: '{}' },
  );
}

// Plan back-link (Slice C §5.2). When a Rollout was spawned from a
// Promotion Plan we surface a "Part of: <Plan>" link in the header. Hits
// the shared /api/plans endpoint directly rather than importing the plans
// module's api.ts — same per-module api.ts discipline as listUsers. 404 →
// null so a since-deleted Plan just hides the back-link.
export async function getRolloutPlanBacklink(
  planId: string,
): Promise<PromotionPlan | null> {
  return maybe(async () => {
    const res = await api<{ plan: PromotionPlan }>(`/api/plans/${planId}`);
    return res.plan;
  });
}

// Users (for AM dropdown) ----------------------------------------------------

export async function listUsers(role?: UserRole): Promise<CurrentUser[]> {
  const qs = role ? `?role=${encodeURIComponent(role)}` : '';
  const res = await api<{ users: CurrentUser[] }>(`/api/users${qs}`);
  return res.users;
}

// Preferences (per-grid presets, Stage 5A §2.2) ------------------------------
// Writes one grid's preferences slice. gridKey namespaces the slice
// ('rollout-master', 'accounts', ...) so each grid's presets persist
// independently on the user doc.

export async function patchMyPreferences(
  gridKey: string,
  preferences: GridPreferences,
): Promise<CurrentUser> {
  const res = await api<{ user: CurrentUser }>(`/api/me/preferences`, {
    method: 'PATCH',
    body: JSON.stringify({ gridKey, preferences }),
  });
  return res.user;
}
